// src/views/ResetPasswordPage.jsx
import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PublicTopBar from "./PublicTopBar";
import Footer from "../components/Footer";
import { confirmerReinitialisationMotDePasse } from "../services/publicAuthService";
import { publicSiteWebService } from "../services/publicSiteWebService";

function evaluerForce(pwd) {
  if (!pwd) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;

  if (score <= 1) return { score: 1, label: "Faible", color: "#C0392B" };
  if (score <= 3) return { score: 2, label: "Moyen", color: "#C98A2B" };
  return { score: 3, label: "Fort", color: "#1A7A41" };
}

export default function ResetPasswordPage() {
  const { slug, userId, token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [succes, setSucces] = useState(false);

  // ====== Données dynamiques (mêmes sources que les autres pages) ======
  const [pageData, setPageData] = useState({
    parametres: null,
    contact: null,
    fonctionnalites: [],
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await publicSiteWebService.getPublicData();
        setPageData({
          parametres: data?.parametres || null,
          contact: data?.contact || null,
          fonctionnalites: data?.fonctionnalites || [],
        });
      } catch (err) {
      }
    };
    loadData();
  }, []);

  // ====== Valeurs extraites (mêmes clés que Features / About / Help) ======
  const params = pageData.parametres || {};
  const contact = pageData.contact || {};

  const nomEditeur = params.nom_editeur || "";
  const nomPlateforme = params.nom_plateforme || nomEditeur || "notre plateforme";
  const couleurPrimaire = params.couleur_primaire || "#004080";
  const couleurSecondaire = params.couleur_secondaire || "#0066CC";

  // 3 fonctionnalités actives (comme Features en affiche)
  const avantages = useMemo(() => {
    return pageData.fonctionnalites
      .filter((f) => f.actif !== false)
      .slice(0, 3)
      .map((f) => ({
        icone: f.icone || "ti-circle-check",
        texte: f.titre || f.description || "",
        couleur: f.couleur || couleurPrimaire,
      }));
  }, [pageData.fonctionnalites, couleurPrimaire]);

  const force = useMemo(() => evaluerForce(password), [password]);

  const regles = useMemo(
    () => [
      { label: "Au moins 8 caractères", ok: password.length >= 8 },
      { label: "Majuscule et minuscule", ok: /[a-z]/.test(password) && /[A-Z]/.test(password) },
      { label: "Au moins un chiffre", ok: /[0-9]/.test(password) },
      { label: "Un caractère spécial", ok: /[^A-Za-z0-9]/.test(password) },
    ],
    [password]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== password2) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);
    try {
      await confirmerReinitialisationMotDePasse(slug, userId, token, password);
      setSucces(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Ce lien est invalide ou a expiré. Demandez un nouveau lien à votre établissement."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        :root {
          --pr: ${couleurPrimaire};
          --pr-light: ${couleurSecondaire};
          --navy-deep: #052A4E;
          --navy-mid: #0B3E75;
          --gold: #C98A2B;
          --bg-soft: #F5F7FA;
          --ink: #17212E;
          --muted: #64748B;
          --line: #E1E7EF;
        }

        .rp-page {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: var(--bg-soft);
        }

        .rp-body {
          flex: 1;
          display: flex;
          padding-top: 72px;
          padding-bottom: 56px;
        }

        /* ================= PANNEAU GAUCHE ================= */
        .rp-brand {
          flex: 1 1 46%;
          position: relative;
          background:
            repeating-linear-gradient(
              to bottom,
              rgba(255,255,255,0.035) 0px,
              rgba(255,255,255,0.035) 1px,
              transparent 1px,
              transparent 34px
            ),
            linear-gradient(155deg, var(--navy-deep) 0%, var(--navy-mid) 60%, var(--pr-light) 100%);
          color: #EAF2FB;
          padding: 64px 56px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow: hidden;
        }

        .rp-brand::after {
          content: "";
          position: absolute;
          width: 520px;
          height: 520px;
          border-radius: 50%;
          background: radial-gradient(circle, ${couleurSecondaire}55 0%, transparent 70%);
          bottom: -220px;
          left: -160px;
          pointer-events: none;
        }

        .rp-brand-inner {
          position: relative;
          max-width: 420px;
          z-index: 1;
          animation: rp-fade-up 0.6s ease-out both;
        }

        .rp-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 20px;
          padding: 6px 12px;
          border: 1px solid rgba(201, 138, 43, 0.35);
          border-radius: 999px;
          background: rgba(201, 138, 43, 0.08);
        }

        .rp-brand-title {
          font-weight: 700;
          font-size: 32px;
          line-height: 1.28;
          margin: 0 0 18px;
          letter-spacing: -0.015em;
          color: #FFFFFF;
        }

        .rp-brand-sub {
          font-size: 15.5px;
          line-height: 1.65;
          color: #C6D8ED;
          margin: 0 0 40px;
        }

        .rp-benefits {
          display: flex;
          flex-direction: column;
          gap: 18px;
          margin-bottom: 40px;
        }

        .rp-benefit {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .rp-benefit i {
          font-size: 18px;
          color: var(--gold);
          margin-top: 2px;
          flex-shrink: 0;
        }

        .rp-benefit span {
          font-size: 14.5px;
          color: #DCE8F6;
          line-height: 1.5;
        }

        .rp-quote {
          padding-left: 16px;
          border-left: 3px solid rgba(201, 138, 43, 0.6);
          font-size: 13.5px;
          font-style: italic;
          color: #B7CCE3;
          line-height: 1.6;
          max-width: 340px;
        }

        /* ---------- Carte flottante (mockup) ---------- */
        .rp-card-mock {
          position: absolute;
          right: -34px;
          top: 54px;
          width: 236px;
          background: #FFFFFF;
          border-radius: 14px;
          padding: 18px 18px 20px;
          box-shadow: 0 24px 48px rgba(2, 16, 33, 0.38);
          transform: rotate(-4deg);
          animation: rp-card-in 0.7s cubic-bezier(.16,1,.3,1) 0.15s both;
          z-index: 2;
        }

        .rp-card-mock-head {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
        }

        .rp-card-avatar {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--pr-light), var(--pr));
          flex-shrink: 0;
        }

        .rp-card-name {
          height: 8px;
          width: 78px;
          border-radius: 4px;
          background: #D8E1EC;
          margin-bottom: 6px;
        }

        .rp-card-sub {
          height: 6px;
          width: 52px;
          border-radius: 4px;
          background: #EAEFF5;
        }

        .rp-card-badge {
          margin-left: auto;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #EAF7EF;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #1A7A41;
          font-size: 12px;
          flex-shrink: 0;
        }

        .rp-card-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
        }

        .rp-card-row:last-child { margin-bottom: 0; }

        .rp-card-label {
          font-size: 10.5px;
          color: #7C8AA0;
          width: 62px;
          flex-shrink: 0;
        }

        .rp-card-bar {
          flex: 1;
          height: 5px;
          border-radius: 3px;
          background: #EEF1F6;
          overflow: hidden;
        }

        .rp-card-bar-fill {
          height: 100%;
          border-radius: 3px;
          animation: rp-bar-grow 1s cubic-bezier(.16,1,.3,1) 0.6s both;
        }

        @keyframes rp-bar-grow {
          from { width: 0 !important; }
        }

        /* ================= PANNEAU DROIT ================= */
        .rp-form-panel {
          flex: 1 1 54%;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px 32px;
        }

        .rp-form-panel::before {
          content: "";
          position: absolute;
          left: 0;
          top: 64px;
          bottom: 64px;
          width: 1px;
          background: linear-gradient(to bottom, transparent, rgba(5, 42, 78, 0.12), transparent);
        }

        .rp-form-inner {
          width: 100%;
          max-width: 400px;
          animation: rp-fade-up 0.6s ease-out 0.1s both;
        }

        .rp-form-title {
          font-weight: 700;
          font-size: 24px;
          color: var(--ink);
          margin: 0 0 8px;
          letter-spacing: -0.01em;
        }

        .rp-form-sub {
          font-size: 14px;
          color: var(--muted);
          line-height: 1.55;
          margin: 0 0 28px;
        }

        .rp-error {
          background: #FCEBEB;
          color: #A32D2D;
          border-radius: 9px;
          padding: 11px 14px;
          font-size: 13.5px;
          margin-bottom: 20px;
          display: flex;
          gap: 8px;
          align-items: flex-start;
          border-left: 3px solid #C0392B;
          animation: rp-shake 0.35s ease;
        }

        @keyframes rp-shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }

        .rp-success {
          background: #EAF7EF;
          border-radius: 14px;
          padding: 32px 24px;
          text-align: center;
          animation: rp-fade-up 0.5s ease-out both;
          border: 1px solid rgba(26, 122, 65, 0.12);
        }

        .rp-success i {
          font-size: 40px;
          color: #1A7A41;
          margin-bottom: 12px;
          display: block;
          animation: rp-pop 0.5s cubic-bezier(.34,1.56,.64,1) 0.15s both;
        }

        @keyframes rp-pop {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .rp-success-title {
          font-weight: 700;
          color: #166034;
          font-size: 17px;
          margin-bottom: 6px;
        }

        .rp-success-sub {
          font-size: 13.5px;
          color: #3E7A56;
        }

        .rp-field { margin-bottom: 18px; }

        .rp-label {
          display: block;
          font-size: 13.5px;
          font-weight: 600;
          color: var(--ink);
          margin-bottom: 7px;
        }

        .rp-input-wrap {
          position: relative;
          display: flex;
        }

        .rp-input {
          width: 100%;
          padding: 11px 42px 11px 14px;
          border: 1.5px solid var(--line);
          border-radius: 9px;
          font-size: 15px;
          font-family: inherit;
          outline: none;
          box-sizing: border-box;
          color: var(--ink);
          background: #FBFCFE;
          transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
        }

        .rp-input:hover { border-color: #C7D2E0; }

        .rp-input:focus {
          border-color: var(--pr-light);
          background: #FFFFFF;
          box-shadow: 0 0 0 3.5px ${couleurSecondaire}26;
        }

        .rp-eye {
          position: absolute;
          right: 4px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: var(--muted);
          font-size: 17px;
          padding: 8px;
          display: flex;
          border-radius: 6px;
          transition: color .15s ease, background .15s ease;
        }

        .rp-eye:hover { color: var(--ink); background: rgba(0,0,0,0.04); }

        .rp-strength {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 9px;
        }

        .rp-strength-track {
          flex: 1;
          display: flex;
          gap: 4px;
        }

        .rp-strength-seg {
          height: 4px;
          flex: 1;
          border-radius: 2px;
          background: var(--line);
          transition: background .25s ease, transform .25s ease;
        }

        .rp-strength-seg.on { transform: scaleY(1.4); }

        .rp-strength-text {
          font-size: 12px;
          font-weight: 600;
          width: 48px;
          text-align: right;
        }

        .rp-rules {
          margin-top: 10px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px 12px;
        }

        .rp-rule {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #94A3B8;
          transition: color .2s ease;
        }

        .rp-rule.ok { color: #1A7A41; }
        .rp-rule i { font-size: 13px; flex-shrink: 0; }

        .rp-hint {
          font-size: 12.5px;
          color: var(--muted);
          margin-top: 7px;
        }

        .rp-match {
          font-size: 12.5px;
          color: #1A7A41;
          margin-top: 7px;
          display: flex;
          align-items: center;
          gap: 5px;
          animation: rp-fade-up 0.25s ease-out both;
        }

        .rp-btn {
          width: 100%;
          padding: 13px;
          background: linear-gradient(180deg, var(--pr-light) 0%, var(--pr) 100%);
          color: #fff;
          border: none;
          border-radius: 9px;
          font-size: 15.5px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          margin-top: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          text-align: center;
          transition: transform .12s ease, box-shadow .15s ease;
          box-shadow: 0 4px 12px -2px ${couleurPrimaire}59;
        }

        .rp-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 18px -4px ${couleurPrimaire}73;
        }

        .rp-btn:disabled { opacity: .72; cursor: default; box-shadow: none; }

        .rp-spin { animation: rp-rotate 0.8s linear infinite; }

        .rp-trust {
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: center;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid var(--line);
          font-size: 12.5px;
          color: var(--muted);
        }

        .rp-trust i { font-size: 15px; color: #1A7A41; }

        @keyframes rp-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes rp-fade-up {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: none; }
        }

        @keyframes rp-card-in {
          from { opacity: 0; transform: translateY(24px) rotate(-1deg) scale(.94); }
          to { opacity: 1; transform: translateY(0) rotate(-4deg) scale(1); }
        }

        @media (prefers-reduced-motion: reduce) {
          .rp-brand-inner, .rp-card-mock, .rp-spin, .rp-form-inner,
          .rp-card-bar-fill, .rp-success, .rp-success i, .rp-error { animation: none; }
        }

        @media (max-width: 900px) {
          .rp-body {
            flex-direction: column;
            padding-top: 64px;
            padding-bottom: 40px;
          }
          .rp-brand { flex: none; padding: 40px 28px 36px; }
          .rp-brand-title { font-size: 26px; }
          .rp-brand-sub { margin-bottom: 24px; }
          .rp-benefits { flex-direction: row; flex-wrap: wrap; gap: 18px; margin-bottom: 24px; }
          .rp-benefit { flex: 1 1 46%; }
          .rp-benefit span { font-size: 13px; }
          .rp-quote { display: none; }
          .rp-card-mock { display: none; }
          .rp-form-panel { padding: 36px 20px 48px; }
          .rp-form-panel::before { display: none; }
        }

        @media (max-width: 480px) {
          .rp-rules { grid-template-columns: 1fr; }
          .rp-form-title { font-size: 22px; }
        }
      `}</style>

      <div className="rp-page">
        <PublicTopBar />

        <div className="rp-body">
          {/* ============ PANNEAU GAUCHE ============ */}
          <div className="rp-brand">
            <div className="rp-card-mock" aria-hidden="true">
              <div className="rp-card-mock-head">
                <div className="rp-card-avatar" />
                <div>
                  <div className="rp-card-name" />
                  <div className="rp-card-sub" />
                </div>
                <div className="rp-card-badge">
                  <i className="ti ti-check" />
                </div>
              </div>
              <div className="rp-card-row">
                <span className="rp-card-label">Assiduité</span>
                <div className="rp-card-bar">
                  <div className="rp-card-bar-fill" style={{ width: "92%", background: "#1A7A41" }} />
                </div>
              </div>
              <div className="rp-card-row">
                <span className="rp-card-label">Moyenne</span>
                <div className="rp-card-bar">
                  <div className="rp-card-bar-fill" style={{ width: "78%", background: couleurSecondaire }} />
                </div>
              </div>
              <div className="rp-card-row">
                <span className="rp-card-label">Messages</span>
                <div className="rp-card-bar">
                  <div className="rp-card-bar-fill" style={{ width: "45%", background: "#C98A2B" }} />
                </div>
              </div>
            </div>

            <div className="rp-brand-inner">
              <div className="rp-eyebrow">
                <i className="ti ti-school" />
                Espace parent
              </div>

              {/* Titre : nom de la plateforme vient du backend */}
              <h1 className="rp-brand-title">
                Bienvenue sur {nomPlateforme} 🎓
              </h1>

              {/* Sous-titre : description générique (pas de champ backend dédié) */}
              <p className="rp-brand-sub">
                Un compte pour suivre la scolarité de votre enfant au quotidien :
                présences, résultats et échanges avec l'établissement, réunis au même endroit.
              </p>

              {/* Avantages : les 3 premières fonctionnalités actives du backend */}
              {avantages.length > 0 && (
                <div className="rp-benefits">
                  {avantages.map((a, i) => (
                    <div className="rp-benefit" key={i}>
                      <i className={`ti ${a.icone}`} />
                      <span>{a.texte}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Citation : nom éditeur */}
              {nomEditeur && (
                <div className="rp-quote">
                  « Un lien de confiance entre {nomEditeur} et les familles. »
                </div>
              )}
            </div>
          </div>

          {/* ============ PANNEAU DROIT ============ */}
          <div className="rp-form-panel">
            <div className="rp-form-inner">
              {succes ? (
                <div className="rp-success" role="status">
                  <i className="ti ti-circle-check" />
                  <div className="rp-success-title">Mot de passe défini avec succès</div>
                  <div className="rp-success-sub">Redirection vers la connexion...</div>
                </div>
              ) : (
                <>
                  <h2 className="rp-form-title">Créez votre mot de passe</h2>
                  <p className="rp-form-sub">
                    Choisissez un mot de passe pour activer votre compte parent.
                  </p>

                  <form onSubmit={handleSubmit} noValidate>
                    {error && (
                      <div className="rp-error" role="alert">
                        <i className="ti ti-alert-circle" />
                        <span>{error}</span>
                      </div>
                    )}

                    <div className="rp-field">
                      <label className="rp-label">Nouveau mot de passe</label>
                      <div className="rp-input-wrap">
                        <input
                          className="rp-input"
                          type={showPassword ? "text" : "password"}
                          placeholder="Au moins 8 caractères"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoFocus
                          required
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="rp-eye"
                          aria-label={showPassword ? "Masquer" : "Afficher"}
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          <i className={`ti ${showPassword ? "ti-eye-off" : "ti-eye"}`} />
                        </button>
                      </div>

                      {password ? (
                        <>
                          <div className="rp-strength">
                            <div className="rp-strength-track">
                              {[1, 2, 3].map((i) => (
                                <div
                                  key={i}
                                  className={`rp-strength-seg ${i <= force.score ? "on" : ""}`}
                                  style={{
                                    background: i <= force.score ? force.color : undefined,
                                  }}
                                />
                              ))}
                            </div>
                            <span className="rp-strength-text" style={{ color: force.color }}>
                              {force.label}
                            </span>
                          </div>

                          <div className="rp-rules">
                            {regles.map((r, idx) => (
                              <div key={idx} className={`rp-rule ${r.ok ? "ok" : ""}`}>
                                <i className={`ti ${r.ok ? "ti-circle-check" : "ti-circle"}`} />
                                <span>{r.label}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="rp-hint">Minimum 8 caractères</div>
                      )}
                    </div>

                    <div className="rp-field">
                      <label className="rp-label">Confirmer le mot de passe</label>
                      <div className="rp-input-wrap">
                        <input
                          className="rp-input"
                          type={showPassword2 ? "text" : "password"}
                          placeholder="Retapez le mot de passe"
                          value={password2}
                          onChange={(e) => setPassword2(e.target.value)}
                          required
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="rp-eye"
                          aria-label={showPassword2 ? "Masquer" : "Afficher"}
                          onClick={() => setShowPassword2((v) => !v)}
                        >
                          <i className={`ti ${showPassword2 ? "ti-eye-off" : "ti-eye"}`} />
                        </button>
                      </div>

                      {password2 && password === password2 && (
                        <div className="rp-match">
                          <i className="ti ti-check" /> Les mots de passe correspondent
                        </div>
                      )}
                    </div>

                    <button type="submit" className="rp-btn" disabled={loading}>
                      {loading ? (
                        <>
                          <i className="ti ti-loader-2 rp-spin" />
                          Activation...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-check" />
                          Activer mon compte
                        </>
                      )}
                    </button>
                  </form>

                  {/* Contact backend si dispo */}
                  <div className="rp-trust">
                    <i className="ti ti-shield-lock" />
                    <span>
                      {contact.email
                        ? `Besoin d'aide ? ${contact.email}`
                        : "Vos informations restent confidentielles"}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}